import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';

const STORAGE_KEY = 'vifm-disclaimer-accepted';

// One-time, blocking acknowledgment shown before the app is usable.
// Educational-use disclaimer + data-source/freshness honesty.
export default function DisclaimerGate() {
  const navigate = useNavigate();
  const [accepted, setAccepted] = useState(() => localStorage.getItem(STORAGE_KEY) === 'true');

  if (accepted) return null;

  function accept() {
    localStorage.setItem(STORAGE_KEY, 'true');
    setAccepted(true);
  }

  function openLegal(path) {
    // Let the user read the detail without dismissing the gate.
    window.open(path, '_blank', 'noopener');
  }

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Disclaimer"
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(1,1,49,0.55)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      }}
    >
      <div
        style={{
          maxWidth: 400, width: '100%', background: 'var(--card, #fff)',
          borderRadius: 16, padding: '28px 22px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        }}
      >
        <div
          style={{
            width: 52, height: 52, borderRadius: 14, margin: '0 auto 16px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(242,166,0,0.12)',
          }}
        >
          <ShieldAlert size={26} style={{ color: '#A37300' }} />
        </div>

        <h2
          className="font-head"
          style={{ fontSize: 19, fontWeight: 700, textAlign: 'center', color: 'var(--navy, #010131)', margin: '0 0 12px' }}
        >
          Before you continue
        </h2>

        <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text-2, #444)', margin: '0 0 12px' }}>
          Ashom is for <strong>educational and informational purposes only</strong>. It is
          <strong> not investment advice</strong>, an offer, or a recommendation to buy or sell any security.
        </p>
        <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text-2, #444)', margin: '0 0 16px' }}>
          Market data is <strong>indicative and may be delayed</strong>. It is sourced from third
          parties (Yahoo Finance and public exchange websites) and is not guaranteed to be accurate,
          complete, or timely. Do not rely on it for trading decisions.
        </p>

        <div style={{ display: 'flex', gap: 14, justifyContent: 'center', marginBottom: 18, flexWrap: 'wrap' }}>
          <button onClick={() => openLegal('/legal/terms')} style={linkStyle}>Terms</button>
          <button onClick={() => openLegal('/legal/privacy')} style={linkStyle}>Privacy</button>
          <button onClick={() => openLegal('/legal/data-sources')} style={linkStyle}>Data sources</button>
        </div>

        <button
          onClick={accept}
          className="font-head"
          style={{
            width: '100%', padding: '13px', borderRadius: 10, border: 'none', cursor: 'pointer',
            background: 'var(--navy, #010131)', color: '#fff', fontSize: 14, fontWeight: 700,
          }}
        >
          I understand &amp; agree
        </button>
      </div>
    </div>,
    document.body
  );
}

const linkStyle = {
  background: 'transparent', border: 'none', cursor: 'pointer', padding: 0,
  fontSize: 12, fontWeight: 600, color: 'var(--blue, #3878BE)', textDecoration: 'underline',
};
